<template>
  <Transition
    name="expand"
    @enter="onEnter"
    @after-enter="onAfterEnter"
    @leave="onLeave">
    <slot />
  </Transition>
</template>

<script setup lang="ts">
  function onEnter(element: Element) {
    if (!(element instanceof HTMLElement)) {
      return
    }

    const width = getComputedStyle(element).width

    element.style.width = width
    element.style.position = 'absolute'
    element.style.visibility = 'hidden'
    element.style.height = 'auto'

    const height = getComputedStyle(element).height

    element.style.width = null
    element.style.position = null
    element.style.visibility = null
    element.style.height = '0'

    // Force repaint to make sure the
    // animation is triggered correctly.
    element.getBoundingClientRect()

    // Trigger the animation.
    // We use `requestAnimationFrame` because we need
    // to make sure the browser has finished
    // painting after setting the `height`
    // to `0` in the line above.
    requestAnimationFrame(() => {
      element.style.height = height
    })
  }

  function onAfterEnter(element: Element) {
    if (!(element instanceof HTMLElement)) {
      return
    }

    element.style.height = 'auto'
  }

  function onLeave(element: Element) {
    if (!(element instanceof HTMLElement)) {
      return
    }

    const height = getComputedStyle(element).height

    element.style.height = height

    // Force repaint to make sure the
    // animation is triggered correctly.
    element.getBoundingClientRect()

    requestAnimationFrame(() => {
      element.style.height = '0'
    })
  }
</script>

<style scoped>
  * {
    will-change: height;
    transform: translateZ(0);
    backface-visibility: hidden;
    perspective: 1000px;
  }

  .expand-enter-active,
  .expand-leave-active {
    @apply overflow-hidden transition-all duration-500 ease-in-out;
  }

  .expand-enter,
  .expand-leave-to {
    @apply h-0 opacity-0;
  }
</style>
